import Card from "@/layout/card";
import Link from "next/link";
import Notification from "@/layout/notification";
import { UserSoundIcon } from "@phosphor-icons/react/dist/ssr";
import Section from "@/layout/section";
import Image from "next/image";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Section1() {
  const { t } = useLanguage();

  return (
    <>
      <Section
        id="for-professionals"
        title={t("section1.title")}
        description={t("section1.description")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex lg:flex-row flex-col md:gap-4 gap-8">
            <Card
              title={t("section1.cards.toolsIntegration.title")}
              description={t("section1.cards.toolsIntegration.description")}
              classNameContent="flex justify-center items-end"
            >
              <div className="absolute top-0 left-0 w-full h-full z-30 p-2 sm:p-4 group-hover:scale-110 lg:group-hover:scale-100 scale-90 lg:group-hover:mt-4 transition-all duration-1000 ease-out">
                <div className="grid grid-cols-2 gap-1 justify-items-center items-center h-full sm:grid-cols-3 sm:gap-2 lg:flex lg:gap-8 lg:justify-center">
                  <Link
                    href="https://www.seca.com/"
                    target="_blank"
                    aria-label="Learn more about Seca medical scales integration"
                    className="flex items-center justify-center w-full h-full min-h-[60px] bg-white/80 rounded-lg p-2 hover:max-md:bg-white/90 transition-colors lg:bg-transparent lg:rounded-none lg:p-0"
                  >
                    <Image
                      src="/images/seca.webp"
                      alt="Seca medical scales integration for healthcare professionals and patient weight tracking"
                      width={80}
                      height={80}
                      loading="lazy"
                      className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px]"
                    />
                  </Link>

                  <Link
                    href="https://www.inbody.com/"
                    target="_blank"
                    aria-label="Learn more about InBody body composition analyzer integration"
                    className="flex items-center justify-center w-full h-full min-h-[60px] bg-white/80 rounded-lg p-2 hover:max-md:bg-white/90 transition-colors lg:bg-transparent lg:rounded-none lg:p-0"
                  >
                    <Image
                      src="/images/inbody.png"
                      alt="InBody body composition analyzer integration for healthcare and nutrition professionals"
                      width={80}
                      height={80}
                      loading="lazy"
                      className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px] lg:-mt-30 lg:-ml-10"
                    />
                  </Link>

                  <Link
                    href="https://www.tanita.com/"
                    target="_blank"
                    aria-label="Learn more about Tanita body composition scale integration"
                    className="flex items-center justify-center w-full h-full min-h-[60px] bg-white/80 rounded-lg p-2 hover:max-md:bg-white/90 transition-colors lg:bg-transparent lg:rounded-none lg:p-0"
                  >
                    <Image
                      src="/images/tanita.png"
                      alt="Tanita body composition scale integration for medical and nutrition practice management"
                      width={80}
                      height={80}
                      loading="lazy"
                      className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px] lg:-mt-48"
                    />
                  </Link>

                  <Link
                    href="https://www.omron.com/"
                    target="_blank"
                    aria-label="Learn more about Omron healthcare device integration"
                    className="flex items-center justify-center w-full h-full min-h-[60px] bg-white/80 rounded-lg p-2 hover:max-md:bg-white/90 transition-colors lg:bg-transparent lg:rounded-none lg:p-0"
                  >
                    <Image
                      src="/images/omron.png"
                      alt="Omron healthcare device integration for blood pressure and health monitoring in medical practice"
                      width={80}
                      height={80}
                      loading="lazy"
                      className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px] lg:-mt-30 lg:-mr-10"
                    />
                  </Link>

                  <Link
                    href="https://www.hologic.com/"
                    target="_blank"
                    aria-label="Learn more about Hologic medical imaging and diagnostics integration"
                    className="flex items-center justify-center w-full h-full min-h-[60px] bg-white/80 rounded-lg p-2 hover:max-md:bg-white/90 transition-colors lg:bg-transparent lg:rounded-none lg:p-0 col-span-2 sm:col-span-1"
                  >
                    <Image
                      src="/images/hologic.png"
                      alt="Hologic medical imaging and diagnostics integration for healthcare practice management"
                      width={80}
                      height={80}
                      loading="lazy"
                      className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px]"
                    />
                  </Link>
                </div>
              </div>
              <div className="h-[170px] w-[170px] rounded-full relative">
                <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-50 scale-[1.5] rounded-full z-10 group-hover:scale-[1.8] group-hover:blur-[1px] transition-all duration-700 ease-out"></div>
                <div className="absolute top-0 left-0 w-full h-full border-3 border-blue-50 scale-[2] rounded-full z-10 group-hover:scale-[2.5] group-hover:blur-[1px] transition-all duration-700 ease-out"></div>
                <div className="absolute top-0 left-0 w-full h-full border-3 border-blue-50/40 scale-[2.5] rounded-full z-10 group-hover:scale-[3.2] group-hover:blur-[1px] transition-all duration-700 ease-out"></div>
                <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-b from-blue-100 to-white border-t-4 border-white rounded-full z-20 group-hover:scale-110 transition-all duration-700 ease-out">
                  <Image
                    src="/images/logo_icon.svg"
                    alt="Hologic medical imaging and diagnostics integration for healthcare practice management"
                    width={80}
                    height={80}
                    loading="lazy"
                    className="object-contain w-16 h-16 sm:w-18 sm:h-18 lg:w-[60px] lg:h-[60px]"
                  />
                </div>
              </div>
            </Card>

            <Card
              title={t("section1.cards.smartFoodLogs.title")}
              description={t("section1.cards.smartFoodLogs.description")}
            >
              <Image
                src="/images/user-food-log.webp"
                alt="Patient food logging interface for nutritionists and healthcare professionals to track meal intake and calories"
                width={300}
                height={600}
                loading="lazy"
                sizes="(max-width: 768px) 230px, 300px"
                className="absolute lg:bottom-[100px] bottom-[150px] right-0 lg:w-[300px] w-[230px] group-hover:bottom-[90px] group-hover:right-4 transition-all duration-700 ease-out"
              />

              <Image
                src="/images/user-food-log.webp"
                alt="Automated nutrition tracking and meal analysis for healthcare professionals managing patient diets"
                width={300}
                height={600}
                loading="lazy"
                sizes="(max-width: 768px) 230px, 300px"
                className="absolute top-[100px] left-0 lg:w-[300px] w-[230px] shadow-xl shadow-black/20 group-hover:left-4 transition-all duration-700 ease-out"
              />
            </Card>
          </div>

          <div className="flex lg:flex-row flex-col md:gap-4 gap-8">
            <Card
              title={t("section1.cards.instantAnswers.title")}
              description={t("section1.cards.instantAnswers.description")}
              className="lg:w-[750px] lg:flex-[750px]"
            >
              <div className="relative w-full flex flex-col gap-4 group-hover:px-2 group-hover:gap-5 transition-all duration-700 ease-out">
                <div className="flex flex-col gap-4 justify-end items-end">
                  <div className="bg-blue-600 w-auto px-4.5 py-2 shadow-xl shadow-blue-600/20 rounded-[20px] text-blue-50 text-sm">
                    {t("section1.chatbot.question1")}
                  </div>
                </div>

                <div className="flex flex-col gap-4 justify-end items-start">
                  <div className="max-w-md bg-white text-slate-700 shadow-xl shadow-slate-300/20 w-auto px-4.5 py-3 rounded-[20px] text-sm">
                    {t("section1.chatbot.answer1")}
                  </div>
                </div>

                <div className="flex flex-col gap-4 justify-end items-end">
                  <div className="bg-blue-600 w-auto px-4.5 py-2 shadow-xl shadow-blue-600/20 rounded-[20px] text-blue-50 text-sm">
                    {t("section1.chatbot.question2")}
                  </div>
                </div>

                <div className="flex flex-col gap-4 justify-end items-start">
                  <div className="max-w-md bg-white text-slate-700 shadow-xl shadow-slate-300/20 w-auto px-4.5 py-3 rounded-[20px] text-sm">
                    {t("section1.chatbot.answer2")}
                  </div>
                </div>
              </div>
            </Card>

            <Card
              title={t("section1.cards.plansBuildOnce.title")}
              description={t("section1.cards.plansBuildOnce.description")}
            >
              <div className="absolute top-14 lg:left-14 -left-4 -rotate-8 group-hover:top-6 group-hover:left-7 transition-all duration-700 ease-out">
                <Image
                  src="/images/iphone-mockup.png"
                  alt="Mobile healthcare app mockup for meal plan management on smartphone"
                  width={200}
                  height={400}
                  loading="lazy"
                  sizes="(max-width: 768px) 180px, 200px"
                  className="lg:w-[200px] w-[180px] relative z-10"
                />

                <Image
                  src="/images/meal-plan.webp"
                  alt="Personalized meal plan interface for nutritionists to create and share with patients"
                  width={180}
                  height={380}
                  loading="lazy"
                  sizes="180px"
                  className="w-[180px] absolute top-[8px] left-[10px] rounded-xl"
                />
              </div>

              <div className="absolute top-4 left-[50%] translate-x-[-50%] z-10 group-hover:top-0 transition-all duration-700 ease-out">
                <Image
                  src="/images/iphone-mockup.png"
                  alt="Healthcare professional mobile app for workout and exercise prescription management"
                  width={1000}
                  height={1000}
                  loading="lazy"
                  className="w-[200px] relative z-10 drop-shadow-2xl drop-shadow-black/30"
                />

                <Image
                  src="/images/workout.webp"
                  alt="Structured workout plan interface for coaches and physical therapists to assign exercises to clients"
                  width={1000}
                  height={1000}
                  loading="lazy"
                  className="w-[180px] absolute top-[8px] lg:left-[10px] left-0 rounded-xl"
                />
              </div>

              <div className="absolute top-14 lg:right-14 -right-8 rotate-8 group-hover:top-6 group-hover:right-7 transition-all duration-700 ease-out z-30">
                <Image
                  src="/images/iphone-mockup.png"
                  alt="Recipe management mobile app for nutrition professionals and healthcare providers"
                  width={1000}
                  height={1000}
                  loading="lazy"
                  className="lg:w-[200px] w-[180px] relative z-10"
                />

                <Image
                  src="/images/recipes.webp"
                  alt="Healthy recipe collection and meal suggestions for patients managed by healthcare professionals"
                  width={1000}
                  height={1000}
                  loading="lazy"
                  className="lg:w-[180px] w-[160px] absolute top-[8px] left-[10px] rounded-xl"
                />
              </div>
            </Card>
          </div>

          <div className="flex lg:flex-row flex-col md:gap-4 gap-8">
            <Card
              title={t("section1.cards.keepEngaged.title")}
              description={t("section1.cards.keepEngaged.description")}
            >
              <div className="flex flex-col w-full gap-4">
                <Notification
                  text={t("section1.notifications.energy")}
                  date="10m ago"
                  className="scale-80 blur-[4px] group-hover:-mt-0 group-hover:scale-100 group-hover:blur-none transition-all duration-700 ease-out"
                />
                <Notification
                  text={t("section1.notifications.hydration")}
                  date="8m ago"
                  className="scale-85 -mt-11 blur-[3px] group-hover:-mt-0 group-hover:scale-100 group-hover:blur-none transition-all duration-700 ease-out"
                />
                <Notification
                  text={t("section1.notifications.workoutReady")}
                  date="5m ago"
                  className="-mt-11 z-10 scale-90 blur-[2px] group-hover:-mt-0 group-hover:scale-100 group-hover:blur-none transition-all duration-700 ease-out"
                />
                <Notification
                  text={t("section1.notifications.mealReady")}
                  date="2m ago"
                  className="-mt-11 z-10 scale-95 blur-[1px] group-hover:-mt-0 group-hover:scale-100 group-hover:blur-none transition-all duration-700 ease-out"
                />
                <Notification
                  text={t("section1.notifications.foodLog")}
                  date="now"
                  className="-mt-11 z-10"
                />
              </div>
            </Card>

            <Card
              title={t("section1.cards.aiScribe.title")}
              description={t("section1.cards.aiScribe.description")}
            >
              <div className="flex flex-col items-center justify-center w-full h-full gap-7">
                <div className="flex w-full justify-end group-hover:-mt-8 group-hover:blur-[3px] transition-all duration-700 ease-out">
                  <div className="bg-blue-50 border border-blue-200/80 shadow-sm shadow-white inset-shadow-sm inset-shadow-slate-50 rounded-2xl p-4 flex flex-col gap-1">
                    <div className="flex gap-1.5 items-center text-xs text-slate-600">
                      <UserSoundIcon />
                      <div>{t("section1.aiScribe.speaker")}</div>
                    </div>
                    <p className="text-sm">{t("section1.aiScribe.intro")}</p>
                  </div>
                </div>

                <div className="flex w-full group-hover:-mt-12 group-hover:ml-8 transition-all duration-700 ease-out z-10">
                  <div className="grid grid-cols-2 gap-2 border border-slate-300 bg-white rounded-2xl p-4 justify-center">
                    <h4 className="col-span-2 text-lg">
                      {t("section1.aiScribe.userInfo")}
                    </h4>
                    <div className="bg-white border border-slate-300 lg:w-[250px] px-3 py-2 rounded-lg flex flex-col leading-4">
                      <span className="text-[10px] text-slate-600">
                        {t("section1.aiScribe.fullName")}
                      </span>
                      <span>John Doe</span>
                    </div>

                    <div className="bg-white border border-slate-300 lg:w-[250px] px-3 py-2 rounded-lg flex flex-col leading-4">
                      <span className="text-[10px] text-slate-600">
                        {t("section1.aiScribe.age")}
                      </span>
                      <span>{t("section1.aiScribe.ageValue")}</span>
                    </div>

                    <div className="bg-white border border-slate-300 lg:w-[250px] px-3 py-2 rounded-lg flex flex-col leading-4">
                      <span className="text-[10px] text-slate-600">
                        {t("section1.aiScribe.currentWeight")}
                      </span>
                      <span>240 lbs</span>
                    </div>

                    <div className="bg-white border border-slate-300 lg:w-[250px] px-3 py-2 rounded-lg flex flex-col leading-4">
                      <span className="text-[10px] text-slate-600">
                        {t("section1.aiScribe.goalWeight")}
                      </span>
                      <span>{t("section1.aiScribe.goalValue")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Section>
    </>
  );
}
